import {
  ColumnsModel,
  QueryBuilderComponent,
  QueryBuilder as QueryBuilderComponentProps,
} from "@syncfusion/ej2-react-querybuilder";
import React, { useEffect } from "react";
import { getColumnConfiguration } from "./ColumnConfiguration";
import {
  AsyncAutoCompleteField,
  EnumMultiSelectFieldV2,
  fieldTypes,
  PrimaryButton,
  TextField,
  utils,
} from "@ucl/library";
import { useState } from "react";
import { GridComponent } from "@syncfusion/ej2-react-grids";
import { TooltipComponent } from "@syncfusion/ej2-react-popups";
import {
  BoltTaskStatuses,
  EventCondition,
  IBoltTask,
  IBoltTaskTriggerField,
  useBoltApiClient,
} from "@bolt/core";

export interface QueryBuilderProps
  extends fieldTypes.BaseFieldProps<EventCondition> {
  queryBuilderRef: React.RefObject<QueryBuilderComponent | null>;
  queryBuilderComponentProps: Omit<
    Partial<QueryBuilderComponentProps>,
    "rule" | "columns"
  >;
  boltTaskRuleId?: string;
  apiBaseUrl: string;
  getAuthRequestHeader: () => Promise<Record<string, string>>;
  canHaveDuplicateStatusesField: {
    data: BoltTaskStatuses[];
    setter: (value: BoltTaskStatuses[]) => void;
  };
}

export const QueryBuilder: React.FC<QueryBuilderProps> = ({
  canHaveDuplicateStatusesField,
  boltTaskRuleId,
  queryBuilderComponentProps,
  queryBuilderRef,
  apiBaseUrl,
  getAuthRequestHeader,
  value,
  onSubmit,
}) => {
  const [localQuery, setLocalQuery] = useState<EventCondition | undefined>(
    value
  );
  const [columnData, setColumnData] = useState<ColumnsModel[]>([]);
  const [queryBuilderKey, setQueryBuilderKey] = useState("");
  const [tasksInEvaluation, _setTasksInEvaluation] = useState<IBoltTask[]>();

  const boltApiClient = useBoltApiClient();

  useEffect(() => {
    if (localQuery?.reportName) {
      boltApiClient
        .get<IBoltTaskTriggerField[]>(
          `odata/bolttasktriggerfieldodata?$filter=domainName eq '${localQuery?.reportName}'`
        )
        .then((fields) =>
          setColumnData(
            fields.map((field) => ({
              field: field.fieldName,
              label: field.fieldLabel,
              type: field.type,
            }))
          )
        )
        .then(() => setQueryBuilderKey(new Date().toISOString()));

      if (onSubmit && value?.reportName != localQuery?.reportName) {
        onSubmit({ ...localQuery, reportName: localQuery?.reportName });
      }
    }
  }, [localQuery?.reportName]);

  return (
    <>
      <TooltipComponent
        content={
          "Select the data source that contains the data element(s) that you would like to evaluate for this trigger."
        }
        target=".task-rule-form-field-grey-background.task-rule-eventbased-datasource"
        position="BottomLeft"
      >
        <div className="task-rule-form-field-grey-background task-rule-eventbased-datasource">
          <AsyncAutoCompleteField
            getAuthRequestHeader={getAuthRequestHeader}
            label="Data Source"
            fieldName="reportName"
            fieldKey="reportNamereportKey"
            odataUrl={`${utils.strings.trimEndForwardSlashForURL(
              apiBaseUrl
            )}/odata/bolttasktriggerdomainodata`}
            fieldNames={["domainName"]}
            autoCompleteSettings={{
              fields: { text: "domainName", value: "domainName" },
              itemTemplate: "${domainName}",
            }}
            value={localQuery?.reportName}
            sortBy="domainName"
            onSubmit={(option) => {
              const domainNameOption = option as unknown as {
                domainName: string;
              };
              if (onSubmit) {
                setLocalQuery({
                  ...localQuery,
                  reportName: domainNameOption.domainName,
                } as EventCondition);
              }
            }}
          />
        </div>
      </TooltipComponent>
      {columnData && (
        <>
          <TooltipComponent
            content={"Add a condition to be evaluated as part of this event."}
            target=".task-rule-eventbased-conditions .e-group-action"
            position="BottomCenter"
          >
            <TooltipComponent
              content={
                'Select "AND" for a task to be created only when ALL of these conditions are met. Select "OR" for a task to be created when ANY of these conditions are met.'
              }
              target=".task-rule-eventbased-conditions .e-btn-group"
              position="BottomCenter"
            >
              <div className="task-rule-form-field-grey-background task-rule-eventbased-conditions">
                <span className="task-rule-report-conditions ucl-label-title">
                  Conditions
                </span>
                <QueryBuilderComponent
                  key={queryBuilderKey}
                  ref={queryBuilderRef}
                  columns={columnData.map((col) =>
                    getColumnConfiguration(col, queryBuilderRef)
                  )}
                  rule={localQuery?.rule}
                  {...queryBuilderComponentProps}
                ></QueryBuilderComponent>
              </div>
            </TooltipComponent>
          </TooltipComponent>
        </>
      )}
      <TooltipComponent
        content={
          'Selecting "Yes" will result in a task being created if the criteria is met, regardless if there is already an existing open task which meets this criteria. Selecting "No" will only create a task if there is not an existing open task which meets this criteria.'
        }
        target=".taskRule-duplicates label.bp5-radio"
        position="BottomCenter"
      >
        <div className="task-rule-form-field-grey-background taskRule-duplicates">
          <EnumMultiSelectFieldV2
            value={canHaveDuplicateStatusesField.data}
            type="EnumMultiSelectV2"
            fieldName="canHaveDuplicateStatuses"
            fieldKey="canHaveDuplicateStatuses"
            label="Allow Duplicates"
            optionValues={utils.enums.convertEnumToOptions(BoltTaskStatuses)}
            onSubmit={() => {
              canHaveDuplicateStatusesField.setter;
            }}
          />
        </div>
      </TooltipComponent>
      <div className="task-rule-form-field-grey-background">
        <TextField
          type="Text"
          fieldName="conditionsActions"
          fieldKey="conditionsActions"
          label="Then"
          value={"Create Task"}
          readOnly={true}
        />
      </div>
      {boltTaskRuleId && (
        <div className="query-builder-playground">
          <PrimaryButton
            text="Evaluate"
            onClick={() => {
              // taskRuleAPIClient
              //   .evaluateEventRule(ruleId)
              //   .then(setTasksInEvaluation);
            }}
          />
          {tasksInEvaluation && (
            <GridComponent
              dataSource={tasksInEvaluation}
              columns={[
                {
                  field: "customerName",
                  headerText: "Customer Name",
                },
                {
                  field: "accountNumber",
                  headerText: "Account Number",
                },
                {
                  field: "dueDate",
                  headerText: "Due Date",
                  template: (row: IBoltTask) => {
                    return utils.date.formatUtcDateTime(
                      row.dueDate || "",
                      true
                    );
                  },
                },
                {
                  field: "stepName",
                  headerText: "Step Name",
                },
              ]}
            />
          )}
        </div>
      )}
    </>
  );
};
